import type { GetLeadDetailsQuery } from '@graphql/__generated__/graphql';
import { PropertySearchesCard } from '@modules/_shared/components/property-search/PropertySearchesCard';
import { LeadDetailsAgentCard } from '@modules/lead-details/components/cards/LeadDetailsAgentCard';
import { LeadDetailsCard } from '@modules/lead-details/components/cards/LeadDetailsCard';
import { LeadDetailsFilesCard } from '@modules/lead-details/components/cards/LeadDetailsFilesCard';
import { LeadDetailsLotCard } from '@modules/lead-details/components/cards/LeadDetailsLotCard';
import { LeadDetailsMarketingCard } from '@modules/lead-details/components/cards/LeadDetailsMarketingCard';
import { LeadDetailsOtherLeadsCard } from '@modules/lead-details/components/cards/LeadDetailsOtherLeadsCard';
import { LeadDetailsProspectionContactCard } from '@modules/lead-details/components/cards/LeadDetailsProspectionContactCard';
import { LeadDetailsQualificationCard } from '@modules/lead-details/components/cards/LeadDetailsQualificationCard';
import { LeadDetailsStatusCard } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import { LeadDetailsValuationCard } from '@modules/lead-details/components/cards/LeadDetailsValuationCard';

type Lead = NonNullable<GetLeadDetailsQuery['leads_by_pk']>;

type LeadDetailsCardStackProps = {
  lead: Lead;
  leadId: string;
  isAdmin: boolean;
  isBrokerageModuleIncluded: boolean;
  restrictedAgentView: boolean;
  canView: boolean;
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string | null) => void;
  requiresAgentSelection: boolean;
  refetch: () => Promise<unknown>;
  onAssignmentChange: (keepOpen: boolean) => void;
  onCmaPreviewGenerated: (reportId: string) => void;
  isPreviewOpen: boolean;
  onClosePreview: () => void;
  requirePropertyForOtherLeads?: boolean;
};

export const LeadDetailsCardStack = ({
  lead,
  leadId,
  isAdmin,
  isBrokerageModuleIncluded,
  restrictedAgentView,
  canView,
  selectedAgentId,
  setSelectedAgentId,
  requiresAgentSelection,
  refetch,
  onAssignmentChange,
  onCmaPreviewGenerated,
  isPreviewOpen,
  onClosePreview,
  requirePropertyForOtherLeads = false,
}: LeadDetailsCardStackProps) => {
  const propertySearches = lead.contact?.property_searches ?? [];
  const contactId = lead.contact?.id;

  const showOtherLeads = requirePropertyForOtherLeads
    ? isAdmin && lead.property != null
    : isAdmin;

  return (
    <>
      <LeadDetailsStatusCard
        leadId={leadId}
        disabled={restrictedAgentView}
        canViewContactDetails={canView}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
        requiresAgentSelection={requiresAgentSelection}
      />
      <LeadDetailsProspectionContactCard leadId={leadId} />
      <LeadDetailsValuationCard
        lead={lead}
        refetch={refetch}
        onCmaPreviewGenerated={onCmaPreviewGenerated}
        isPreviewOpen={isPreviewOpen}
        onClosePreview={onClosePreview}
      />
      <LeadDetailsCard lead={lead} />
      <LeadDetailsAgentCard
        leadId={leadId}
        onAssignmentChange={onAssignmentChange}
      />
      <LeadDetailsQualificationCard
        leadId={leadId}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
      />
      {isBrokerageModuleIncluded && <LeadDetailsLotCard lead={lead} />}
      {(isAdmin ||
        (lead.source?.name !== 'appraisal' && lead.source?.name !== 'crm')) && (
        <LeadDetailsMarketingCard lead={lead} />
      )}
      {showOtherLeads && <LeadDetailsOtherLeadsCard leadId={leadId} />}
      <PropertySearchesCard
        propertySearches={propertySearches}
        onUpdate={refetch}
        onDelete={refetch}
        onCreate={refetch}
        prefilledUserId={contactId}
      />
      <LeadDetailsFilesCard lead={lead} />
    </>
  );
};
